import React from 'react';

export interface InvoiceStatusData {
  status: string;
  count: number;
  amount: number;
  color?: string;
}

export interface InvoiceStatusBreakdownProps {
  data: InvoiceStatusData[];
  isLoading?: boolean;
  size?: number;
}

const InvoiceStatusBreakdown: React.FC<InvoiceStatusBreakdownProps> = ({ data, isLoading, size = 250 }) => {
  const defaultColors = ['#10b981', '#f59e0b', '#ef4444', '#6b7280', '#3b82f6'];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Breakdown</h2>
        <div className="flex items-center justify-center" style={{ height: `${size}px` }}>
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Breakdown</h2>
        <div className="flex items-center justify-center" style={{ height: `${size}px` }}>
          <div className="text-gray-500">No invoice data available</div>
        </div>
      </div>
    );
  }

  const totalCount = data.reduce((sum, item) => sum + item.count, 0);
  const totalAmount = data.reduce((sum, item) => sum + item.amount, 0);

  if (totalCount === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Breakdown</h2>
        <div className="flex items-center justify-center" style={{ height: `${size}px` }}>
          <div className="text-gray-500">No invoices found</div>
        </div>
      </div>
    );
  }

  const radius = size / 2 - 20;
  const centerX = size / 2;
  const centerY = size / 2;
  const innerRadius = radius * 0.6;

  let currentAngle = -90;

  const paths = data.map((item, index) => {
    const percentage = item.count / totalCount;
    const angle = (percentage * 360);
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const color = item.color || defaultColors[index % defaultColors.length];

    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + radius * Math.cos(startAngleRad);
    const y1 = centerY + radius * Math.sin(startAngleRad);
    const x2 = centerX + radius * Math.cos(endAngleRad);
    const y2 = centerY + radius * Math.sin(endAngleRad);

    const x1Inner = centerX + innerRadius * Math.cos(startAngleRad);
    const y1Inner = centerY + innerRadius * Math.sin(startAngleRad);
    const x2Inner = centerX + innerRadius * Math.cos(endAngleRad);
    const y2Inner = centerY + innerRadius * Math.sin(endAngleRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const outerPath = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
    const innerPath = `L ${x2Inner} ${y2Inner} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x1Inner} ${y1Inner} Z`;

    const pathData = `${outerPath} ${innerPath}`;

    const labelAngle = (startAngle + endAngle) / 2;
    const labelAngleRad = (labelAngle * Math.PI) / 180;
    const labelRadius = (radius + innerRadius) / 2;
    const labelX = centerX + labelRadius * Math.cos(labelAngleRad);
    const labelY = centerY + labelRadius * Math.sin(labelAngleRad);

    return {
      pathData,
      color,
      percentage,
      count: item.count,
      amount: item.amount,
      status: item.status,
      labelX,
      labelY,
    };
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Breakdown</h2>
      <div className="flex flex-col lg:flex-row gap-6 items-center">
        <div className="flex-shrink-0">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
            {paths.map((path, index) => (
              <g key={index}>
                <path
                  d={path.pathData}
                  fill={path.color}
                  stroke="white"
                  strokeWidth="2"
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <title>
                    {path.status}: {path.count} invoices (€{path.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                  </title>
                </path>
                {path.percentage > 0.05 && (
                  <text
                    x={path.labelX}
                    y={path.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="text-xs font-medium fill-white pointer-events-none"
                  >
                    {((path.percentage) * 100).toFixed(0)}%
                  </text>
                )}
              </g>
            ))}
          </svg>
        </div>
        <div className="flex-1 space-y-2 w-full">
          {data.map((item, index) => {
            const color = item.color || defaultColors[index % defaultColors.length];
            const percentage = (item.count / totalCount) * 100;
            return (
              <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className="w-4 h-4 rounded flex-shrink-0"
                    style={{ backgroundColor: color }}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-700 block truncate">{item.status}</span>
                    <span className="text-xs text-gray-500">
                      €{item.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-sm font-medium text-gray-900">{item.count}</span>
                  <span className="text-xs text-gray-500 w-12 text-right">{percentage.toFixed(1)}%</span>
                </div>
              </div>
            );
          })}
          <div className="pt-2 border-t border-gray-200 mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-gray-900">Total Invoices</span>
              <span className="text-sm font-semibold text-gray-900">{totalCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">Total Amount</span>
              <span className="text-sm font-semibold text-gray-900">
                €{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceStatusBreakdown;

